import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib/cn";

const loaderVariants = cva(
  "animate-spin rounded-full border-2 border-current border-t-transparent",
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        md: "h-6 w-6",
        lg: "h-8 w-8",
        xl: "h-12 w-12",
      },
      color: {
        primary: "text-primary",
        white: "text-white",
        muted: "text-muted-foreground",
      },
    },
    defaultVariants: {
      size: "md",
      color: "primary",
    },
  }
);

export interface LoaderProps extends VariantProps<typeof loaderVariants> {
  className?: string;
  label?: string;
}

function Loader({ size, color, className, label = "Loading..." }: LoaderProps) {
  return (
    <div role="status" aria-label={label} className="inline-flex items-center justify-center">
      <span className={cn(loaderVariants({ size, color }), className)} aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </div>
  );
}

function PageLoader() {
  return (
    <div className="flex h-full min-h-[200px] w-full items-center justify-center">
      <Loader size="lg" />
    </div>
  );
}

function FullScreenLoader({ message }: { message?: string }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm"
      role="status"
      aria-label={message ?? "Loading..."}
    >
      <Loader size="xl" />
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
    </div>
  );
}

export { Loader, PageLoader, FullScreenLoader };
